import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useTeamMembers, useUpdateMember, useRemoveMember, useRoles } from "@/hooks/useTeam";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { InviteMemberWizard } from "./InviteMemberWizard";
import RoleSelector from "../roles/RoleSelector";
import { Users, Trash2, UserPlus } from "lucide-react";
import { format } from "date-fns";

interface TeamMembersTabProps {
  accountId: string;
  workspaceId: string;
}

export function TeamMembersTab({ accountId, workspaceId }: TeamMembersTabProps) {
  const { data: members, isLoading: loadingMembers } = useTeamMembers(accountId);
  const { data: roles, isLoading: loadingRoles } = useRoles(accountId);
  const updateMember = useUpdateMember();
  const removeMember = useRemoveMember();
  
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const handleRemove = async () => {
    if (!memberToRemove) return;
    
    try {
      await removeMember.mutateAsync({ accountId, memberId: memberToRemove });
      showSuccess("Team member has been removed successfully.", "Member removed");
      setMemberToRemove(null);
    } catch (error) {
      showError("Failed to remove team member.", "Error");
    }
  };

  if (loadingMembers) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!members?.length) {
    return (
      <>
        <Button onClick={() => setWizardOpen(true)} className="mb-4">
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Team Member
        </Button>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No team members yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Click "Invite Team Member" to add members to your team
              </p>
            </div>
          </CardContent>
        </Card>

        <InviteMemberWizard
          workspaceId={workspaceId}
          open={wizardOpen}
          onOpenChange={setWizardOpen}
        />
      </>
    );
  }

  return (
    <>
      <Button onClick={() => setWizardOpen(true)} className="mb-4">
        <UserPlus className="w-4 h-4 mr-2" />
        Invite Team Member
      </Button>

      <div className="space-y-4">
        {members.map((member) => (
          <Card key={member.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {member.firstName || member.username}
                  </CardTitle>
                  <CardDescription>{member.email}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMemberToRemove(member.id)}
                  disabled={removeMember.isPending}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {member.joinedAt ? (
                    <>Joined {format(new Date(member.joinedAt), "MMM d, yyyy")}</>
                  ) : (
                    "Member"
                  )}
                </div>
                <RoleSelector
                  memberId={member.id}
                  currentRole={member.roleName || 'member'}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <InviteMemberWizard
        workspaceId={workspaceId}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
      />

      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the member from your team. They will lose access to your workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
